import React, { useState } from 'react';
import * as yup from 'yup';

import SidePanelContentSection from '../../../common2/side-panel/SidePanelContentSection';
import { ClaimContext, ClaimFileType, ClaimType, OnChangeClaimContext } from '../types';
import { ClaimStepContainerProps } from './ClaimStepContainer';
import { useValidateStep } from './hooks/useValidateStep';
import WeightInput from './WeightInput';
import { Input } from '../../../common2/Input/Input';
import ColumnsLayout from '../../../common/layout/ColumnsLayout';
import useOnFormChange from '../../../../hooks/useOnFormChange';
import FileImportMessage from './FileImportMessage';
import { ImportFileState } from './useFileImport';
import LoadingStep from './LoadingStep';
import Uploader from '../../../common/Uploader';
import InputHelpLink from '../InputHelpLink';
import Column from '../../../common/layout/Column';
import ItemPicturesUploader from './ItemPicturesUploader';
import TextArea from '../../../common2/TextArea';
import { computeAdditionalInfoRequirements } from '../claimsHelpers';
import './yupClaimsExtensions';
import { useClaimsShopSettings } from '../../ClaimsShopSettingsContext';

export type ClaimContextForAdditionalInfoStep = Pick<
  ClaimContext,
  | 'submissionInfo'
  | 'weight'
  | 'recipientPhoneNumber'
  | 'carrier'
  | 'declarationOfRecipient'
  | 'lmcCaseId'
  | 'carrierDamageReport'
  | 'type'
  | 'damageInfo'
  | 'isDelivered'
  | 'proofOfConsumerPayment'
  | 'deliveryDate'
  | 'postalReturnDeliveryDate'
  | 'carrierCode'
  | 'carrierCountryCode'
  | 'picturesOfDamage'
  | 'picturesOfMissingItem'
  | 'shopSettings'
  | 'claimRequirements'
  | 'dataProcessingConsent'
>;

export type AdditionalInfoStepProps = {
  claim: ClaimContextForAdditionalInfoStep;
  as: React.ComponentType<ClaimStepContainerProps>;
  onChange: OnChangeClaimContext;
  importDORState: ImportFileState;
} & ClaimStepContainerProps;

const schema = yup.object({
  weight: yup
    .number()
    .draftablePositive()
    .empty('')
    .label('Weight')
    .when('$isWeightRequired', {
      is: true,
      then: (schema) => schema.draftableRequired(),
      otherwise: (schema) => schema.notRequired().nullable(),
    }),
  recipientPhoneNumber: yup
    .string()
    .label('Recipient phone number')
    .when('$isRecipientPhoneNumberRequired', {
      is: true,
      then: (schema) => schema.draftableRequired(),
      otherwise: (schema) => schema.notRequired().nullable(),
    }),
  declarationOfRecipient: yup
    .array()
    .when('$isDORRequired', {
      is: true,
      then: (schema) => schema.draftableMinOneAsRequired().max(1),
    })
    .label('Declaration of recipient'),
  carrierDamageReport: yup
    .array()
    .when('$isCarrierCaseIdAndCarrierDamageReportRequired', {
      is: true,
      then: (schema) => schema.draftableMinOneAsRequired().max(1),
    })
    .label('Carrier damage report'),
  lmcCaseId: yup
    .string()
    .max(64)
    .label('Carrier case ID')
    .when('$isCarrierCaseIdAndCarrierDamageReportRequired', {
      is: true,
      then: (schema) => schema.draftableRequired(),
      otherwise: (schema) => schema.notRequired().nullable(),
    }),
  picturesOfDamage: yup
    .array()
    .when('$isDamagePhotosRequired', {
      is: true,
      then: (schema) => schema.draftableMinOneAsRequired(),
    })
    .label('Pictures of damage'),
  damageInfo: yup
    .string()
    .label('Damage info')
    .when('$isDamageInfoRequired', {
      is: true,
      then: (schema) => schema.draftableRequired(),
      otherwise: (schema) => schema.nullable(),
    }),
  picturesOfMissingItem: yup
    .array()
    .when('$isPicturesOfMissingItemRequired', {
      is: true,
      then: (schema) => schema.draftableMinOneAsRequired(),
    })
    .label('Pictures of missing item'),
  proofOfConsumerPayment: yup.array().max(1).label('Proof of consumer payment'),
  dataProcessingConsent: yup
    .array()
    .when('$isDataProcessingConsentRequired', {
      is: true,
      then: (schema) => schema.draftableMinOneAsRequired().max(1),
    })
    .label('Data processing consent'),
});

function AdditionalInfoStep(props: AdditionalInfoStepProps) {
  const { claim, onChange, onNext, as: Container, importDORState, ...restProps } = props;

  const shopSettings = useClaimsShopSettings();
  const { recipientPhoneNumber, weight, carrierDamageReport, lmcCaseId, damageInfo, type } = claim;
  const onInputChange = useOnFormChange(onChange);
  const [isNextButtonDisabled, setIsNextButtonDisabled] = useState(false);

  const isProofOfConsumerPaymentUploaderShown = type === ClaimType.CASH_ON_DELIVERY;
  const isPicturesOfMissingItemUploaderShown = type === ClaimType.MISSING_ITEM;
  const isDhlDe = claim.carrierCode === 'dhl' && claim.carrierCountryCode === 'de';

  const {
    isWeightRequired,
    isRecipientPhoneNumberRequired,
    isCarrierCaseIdAndCarrierDamageReportRequired,
    isDamageInfoRequired,
    isDamagePhotosRequired,
    isPicturesOfMissingItemRequired,
    isCarrierSpecificDataRequired,
    declarationOfRecipientRequirement,
    isDataProcessingConsentRequired,
  } = computeAdditionalInfoRequirements({ ...claim, shopSettings });

  const validationContext = {
    isWeightRequired,
    isRecipientPhoneNumberRequired,
    isDORRequired: declarationOfRecipientRequirement === 'required',
    isCarrierCaseIdAndCarrierDamageReportRequired: isCarrierCaseIdAndCarrierDamageReportRequired && !isDhlDe,
    isDamageInfoRequired,
    isDamagePhotosRequired,
    isPicturesOfMissingItemRequired,
    isDataProcessingConsentRequired,
  };

  const { onClickSaveDraft, onClickNext, onClickBack, errors } = useValidateStep(
    schema,
    claim,
    validationContext,
    props
  );

  if (declarationOfRecipientRequirement !== 'none' && importDORState === 'searching') {
    return <LoadingStep {...props} text="CLAIMS_IMPORT_DOR_SEARCHING" />;
  }

  return (
    <Container
      {...restProps}
      onSaveDraft={onClickSaveDraft}
      onNext={onClickNext}
      onBack={onClickBack}
      isDisabled={isNextButtonDisabled}
    >
      {declarationOfRecipientRequirement !== 'none' && (
        <FileImportMessage state={importDORState} fileType={ClaimFileType.DECLARATION_OF_RECIPIENT} />
      )}
      <Column>
        {isDamageInfoRequired && (
          <SidePanelContentSection heading="DAMAGE_CLAIM_SECTION_HEADER">
            <Column>
              <ItemPicturesUploader
                name="picturesOfDamage"
                label="Pictures of damage"
                isRequired={isDamagePhotosRequired}
                helpText="CLAIMS_DAMAGE_PICTURES_HELP_TEXT"
                claim={claim}
                errors={errors}
                onChange={onInputChange}
                onBusy={setIsNextButtonDisabled}
                uploaderHelpLinkElement={
                  // eslint-disable-next-line max-len
                  <InputHelpLink to="https://support.portal.sevensenders.com/support/solutions/articles/15000048062-submission-document-requirements#Damage-Photo-Requirements%C2%A0">
                    Damage photo requirements
                  </InputHelpLink>
                }
              />
              <TextArea
                label="DAMAGE_DESC"
                value={damageInfo}
                onChange={onInputChange}
                name="damageInfo"
                helpText="DAMAGE_TEXT_AREA_HELP"
                error={errors.damageInfo}
                isRequired
                noErrorTranslation
              />
            </Column>
          </SidePanelContentSection>
        )}
        {isProofOfConsumerPaymentUploaderShown && (
          <Uploader
            name="proofOfConsumerPayment"
            label="CLAIMS_DOCUMENTS_UPLOAD_POCP"
            topRightElement={
              // eslint-disable-next-line max-len
              <InputHelpLink to="https://support.portal.sevensenders.com/support/solutions/articles/15000048062-submission-document-requirements">
                Proof of consumer payment requirements
              </InputHelpLink>
            }
            files={claim.proofOfConsumerPayment}
            errorWithTranslation={errors.proofOfConsumerPayment}
            allowedFileTypes={['application/pdf', 'image/png', 'image/jpg', 'image/jpeg']}
            helpText="CLAIMS_DOCUMENTS_UPLOAD_POCP_HELP_TEXT"
            onChange={onInputChange}
            onBusy={setIsNextButtonDisabled}
            maxNumberOfFiles={1}
          />
        )}
        {isPicturesOfMissingItemUploaderShown && (
          <SidePanelContentSection heading="MISSING_ITEM_SECTION_HEADER">
            <ItemPicturesUploader
              name="picturesOfMissingItem"
              label="MISSING_ITEM_PICTURES_UPLOADER_LABEL"
              helpText="CLAIMS_DAMAGE_PICTURES_HELP_TEXT"
              claim={claim}
              errors={errors}
              onChange={onInputChange}
              onBusy={setIsNextButtonDisabled}
              isRequired={isPicturesOfMissingItemRequired}
            />
          </SidePanelContentSection>
        )}
        {declarationOfRecipientRequirement !== 'none' && (
          <SidePanelContentSection heading="DOR_SECTION_HEADER">
            <Uploader
              name="declarationOfRecipient"
              label="CLAIMS_DOCUMENTS_UPLOAD_DOR"
              topRightElement={
                // eslint-disable-next-line max-len
                <InputHelpLink to="https://support.portal.sevensenders.com/support/solutions/articles/15000048062-submission-document-requirements#Declaration-of-Receipt-(DOR)-Requirements%C2%A0">
                  DOR requirements
                </InputHelpLink>
              }
              isRequired={declarationOfRecipientRequirement === 'required'}
              files={claim.declarationOfRecipient}
              errorWithTranslation={errors.declarationOfRecipient}
              allowedFileTypes={['application/pdf', 'image/png', 'image/jpg', 'image/jpeg']}
              helpText="CLAIMS_DOCUMENTS_UPLOAD_DOR_HELP_TEXT"
              onChange={onInputChange}
              onBusy={setIsNextButtonDisabled}
              maxNumberOfFiles={1}
            />
          </SidePanelContentSection>
        )}
        {isCarrierSpecificDataRequired && (
          <SidePanelContentSection heading="CARRIER_SPECIFIC_REQUIREMENTS">
            <ColumnsLayout>
              {isWeightRequired && (
                <WeightInput
                  name="weight"
                  label="Weight"
                  value={weight}
                  error={errors.weight}
                  onChange={onInputChange}
                  noErrorTranslation
                  isRequired
                />
              )}
              {isRecipientPhoneNumberRequired && (
                <Input
                  name="recipientPhoneNumber"
                  label="Recipient phone number"
                  placeholder="Add recipient phone number"
                  isRequired
                  value={recipientPhoneNumber}
                  error={errors.recipientPhoneNumber}
                  onChange={onInputChange}
                  noErrorTranslation
                />
              )}
            </ColumnsLayout>
            {isDataProcessingConsentRequired && (
              <Uploader
                name="dataProcessingConsent"
                label="CLAIMS_DOCUMENTS_UPLOAD_DATA_PROCESSING_CONSENT"
                isRequired
                files={claim.dataProcessingConsent}
                errorWithTranslation={errors.dataProcessingConsent}
                helpText="CLAIMS_DOCUMENTS_UPLOAD_DATA_PROCESSING_CONSENT_HELP"
                allowedFileTypes={['application/pdf', 'image/png', 'image/jpg', 'image/jpeg']}
                onChange={onInputChange}
                onBusy={setIsNextButtonDisabled}
                maxNumberOfFiles={1}
              />
            )}
            {isCarrierCaseIdAndCarrierDamageReportRequired && (
              <Column>
                <ColumnsLayout>
                  <Input
                    isRequired={!isDhlDe}
                    name="lmcCaseId"
                    value={lmcCaseId}
                    label="CARRIER_CASE_ID_LABEL"
                    placeholder="Add carrier case ID"
                    error={errors.lmcCaseId}
                    onChange={onInputChange}
                  />
                </ColumnsLayout>
                <Uploader
                  name="carrierDamageReport"
                  files={carrierDamageReport}
                  label="Carrier damage report"
                  isRequired={!isDhlDe}
                  errorWithTranslation={errors.carrierDamageReport}
                  allowedFileTypes={['application/pdf', 'image/jpg', 'image/jpeg', 'image/png']}
                  helpText="CLAIMS_DOCUMENTS_UPLOAD_CARRIER_DAMAGE_REPORT_HELP"
                  onChange={onInputChange}
                  onBusy={setIsNextButtonDisabled}
                  maxNumberOfFiles={1}
                />
              </Column>
            )}
          </SidePanelContentSection>
        )}
      </Column>
    </Container>
  );
}

export default React.memo(AdditionalInfoStep);
